import React, { useEffect, useState } from "react";
import { Save, Building2, Bot, Sparkles, Users, Check, BookOpen, Plus, X, HardDrive, Cloud, Download, ChevronDown, ChevronUp, Link2, Unlink, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function Section({ icon: Icon, title, desc, children }) {
    return (
        <div className="card-tpl p-6">
            <div className="flex items-start gap-3 mb-1">
                <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: "var(--mod-accent-soft)" }}>
                    <Icon size={18} style={{ color: "var(--mod-accent)" }} />
                </div>
                <div>
                    <h3 className="text-[16px] font-extrabold tracking-tight" style={{ color: "var(--navy)" }}>{title}</h3>
                    {desc && <p className="text-[13px] mt-0.5" style={{ color: "var(--text-2)" }}>{desc}</p>}
                </div>
            </div>
            <div className="mt-5">{children}</div>
        </div>
    );
}

function Label({ children, hint }) {
    return (
        <div className="flex items-center justify-between mb-1.5">
            <span className="mono text-[10px] uppercase tracking-[0.1em] font-bold" style={{ color: "var(--text)" }}>{children}</span>
            {hint && <span className="text-[11px]" style={{ color: "var(--muted)" }}>{hint}</span>}
        </div>
    );
}

const STORAGE_OPTIONS = [
    {
        id: "manual",
        name: "Download manuale",
        tagline: "Più semplice · Nessun account cloud · Sempre disponibile",
        icon: Download,
        color: "#0F1F3D",
        bestFor: "Inizia subito. I file sono scaricabili dalla scheda commessa in 1 click.",
        pros: ["Zero configurazione", "Niente account esterni", "Funziona subito"],
        cons: ["Devi scaricare manualmente ogni file", "I file scadono dopo 24h"],
        steps: [
            "Già attivo, niente da configurare.",
            "Quando arriva un ordine dalla landing o da Telegram, apri la commessa nella dashboard.",
            "Clicca su 'Scarica file' per salvare i file sul tuo computer.",
            "I file restano disponibili per 24 ore. Scaricali subito per non perderli.",
        ],
    },
    {
        id: "gdrive",
        name: "Google Drive",
        tagline: "Consigliato · File anche su HD del tuo PC · Backup automatico",
        icon: Cloud,
        color: "#1E40AF",
        bestFor: "Vuoi vedere i file ANCHE sul disco del PC senza scaricarli a mano. Drive Desktop sincronizza tutto.",
        pros: [
            "I file appaiono in una cartella del PC (es. C:\\Google Drive\\TotalPL)",
            "Backup automatico su cloud + accesso da telefono/tablet",
            "15GB gratis, poi €1.99/mese per 100GB",
            "Condivisi con i tuoi collaboratori",
        ],
        cons: ["Serve un account Google", "Solo i file creati da TotalPL sono visibili al sistema (scope drive.file)"],
        steps: [
            "Clicca 'Collega Google Drive' qui sotto: si apre Google, accedi col tuo account.",
            "Consenti a TotalPL di salvare file nel tuo Drive (vedrai 'TotalPL vuole creare e gestire i file Drive specifici').",
            "Torni qui automaticamente: stato 'Collegato ✓'.",
            "Da quel momento i file dei nuovi ordini finiscono in Drive → cartella 'TotalPL/{numero-cliente}/'.",
            "Opzionale: scarica Google Drive Desktop da https://www.google.com/drive/download/ per avere i file SUL PC sincronizzati in tempo reale.",
        ],
        oauth: true,
    },
    {
        id: "dropbox",
        name: "Dropbox",
        tagline: "Alternativa a Drive · Stesso comportamento",
        icon: Cloud,
        color: "#0061FF",
        bestFor: "Usi già Dropbox per altri lavori? Stesso principio di Drive: i file appaiono sul PC.",
        pros: ["Cartella sincronizzata sul PC", "2GB gratis (€10/mese per 2TB)", "Condivisione facile"],
        cons: ["Spazio gratuito limitato", "Integrazione disponibile dalla Fase 4"],
        steps: [
            "Scarica Dropbox da: https://www.dropbox.com/install",
            "Installalo, accedi con il tuo account.",
            "Crea una cartella 'TotalPL' nel tuo Dropbox.",
            "Torna qui e clicca 'Collega Dropbox' (disponibile a breve).",
            "I file delle commesse appariranno automaticamente in C:\\…\\Dropbox\\TotalPL\\.",
        ],
        comingSoon: true,
    },
    {
        id: "onedrive",
        name: "Microsoft OneDrive",
        tagline: "Ideale se usi già Office 365",
        icon: Cloud,
        color: "#0078D4",
        bestFor: "Hai un account Microsoft/Office 365? OneDrive è già installato su Windows 10/11.",
        pros: ["Pre-installato su Windows", "5GB gratis, 1TB con Microsoft 365", "Sincronizzazione veloce"],
        cons: ["Limitato a 5GB nel piano gratuito", "Integrazione disponibile dalla Fase 4"],
        steps: [
            "Su Windows 10/11 OneDrive è già installato. Cerca l'icona della nuvola in basso a destra.",
            "Accedi con il tuo account Microsoft (o creane uno gratuito).",
            "Crea una cartella 'TotalPL' nel tuo OneDrive.",
            "Torna qui e clicca 'Collega OneDrive' (disponibile a breve).",
            "I file appariranno in C:\\Users\\…\\OneDrive\\TotalPL\\.",
        ],
        comingSoon: true,
    },
];

function StorageCard({ opt, selected, onSelect, oauthStatus, onConnect, onDisconnect, busy }) {
    const [open, setOpen] = useState(false);
    const Icon = opt.icon;
    const isSelected = selected === opt.id;
    const connected = opt.oauth && oauthStatus?.connected;
    return (
        <div className="rounded-xl transition-all" style={{
            border: isSelected ? `2px solid ${opt.color}` : "1px solid var(--line)",
            background: isSelected ? `${opt.color}06` : "#fff",
            boxShadow: isSelected ? `0 4px 16px ${opt.color}22` : "none",
        }} data-testid={`storage-card-${opt.id}`}>
            <div className="p-5">
                <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-lg grid place-items-center text-white" style={{ background: opt.color }}>
                            <Icon size={20} />
                        </div>
                        <div>
                            <div className="font-extrabold text-[15px] flex items-center gap-2" style={{ color: "var(--navy)" }}>
                                {opt.name}
                                {connected && (
                                    <span className="mono text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full flex items-center gap-1" style={{ background: "#10B98115", color: "#065F46" }} data-testid={`storage-connected-${opt.id}`}>
                                        <Check size={9} /> Collegato
                                    </span>
                                )}
                            </div>
                            <div className="text-[12px]" style={{ color: "var(--muted)" }}>{opt.tagline}</div>
                        </div>
                    </div>
                    {isSelected ? (
                        <span className="mono text-[10px] uppercase tracking-wider font-extrabold px-2.5 py-1 rounded-full text-white flex items-center gap-1" style={{ background: opt.color }} data-testid={`storage-active-${opt.id}`}>
                            <Check size={11} /> Attivo
                        </span>
                    ) : (
                        <button onClick={() => onSelect(opt.id)} disabled={opt.comingSoon} className="px-3 py-1.5 rounded-lg text-[12px] font-bold transition disabled:opacity-50" style={{
                            background: opt.comingSoon ? "var(--bg)" : opt.color,
                            color: opt.comingSoon ? "var(--muted)" : "#fff",
                        }} data-testid={`storage-select-${opt.id}`}>
                            {opt.comingSoon ? "Prossimamente" : "Scegli"}
                        </button>
                    )}
                </div>
                <p className="text-[12.5px] mt-3" style={{ color: "var(--text-2)" }}>{opt.bestFor}</p>

                {opt.oauth && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {!connected ? (
                            <button onClick={onConnect} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-bold text-white disabled:opacity-50" style={{ background: opt.color }} data-testid={`storage-connect-${opt.id}`}>
                                {busy ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />}
                                {busy ? "Apertura..." : "Collega Google Drive"}
                            </button>
                        ) : (
                            <>
                                <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-[12.5px] font-bold" style={{ background: "#10B98115", color: "#065F46" }}>
                                    <Check size={13} /> Drive collegato {oauthStatus?.connected_at && (<span className="opacity-60">· {new Date(oauthStatus.connected_at).toLocaleDateString("it-IT")}</span>)}
                                </span>
                                <button onClick={onDisconnect} disabled={busy} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-[12.5px] font-bold disabled:opacity-50" style={{ background: "var(--bg)", color: "#B91C1C", border: "1px solid #FCA5A5" }} data-testid={`storage-disconnect-${opt.id}`}>
                                    <Unlink size={13} /> Scollega
                                </button>
                            </>
                        )}
                    </div>
                )}

                {opt.comingSoon && (
                    <div className="mt-3 px-3 py-2 rounded text-[11.5px] flex items-center gap-2" style={{ background: "#FEF3C7", color: "#92400E" }}>
                        <Sparkles size={12} /> Integrazione automatica disponibile dalla Fase 4. Le istruzioni qui sotto ti permettono di prepararti.
                    </div>
                )}

                <button onClick={() => setOpen(o => !o)} className="mt-3 inline-flex items-center gap-1 text-[12px] font-bold" style={{ color: opt.color }} data-testid={`storage-toggle-${opt.id}`}>
                    {open ? <><ChevronUp size={13} /> Nascondi istruzioni</> : <><ChevronDown size={13} /> Mostra istruzioni passo-passo</>}
                </button>

                {open && (
                    <div className="mt-4 grid md:grid-cols-2 gap-4">
                        <div>
                            <div className="mono text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: "var(--green, #10B981)" }}>✓ Vantaggi</div>
                            <ul className="space-y-1">
                                {opt.pros.map((p, i) => (
                                    <li key={i} className="text-[12.5px] flex items-start gap-1.5" style={{ color: "var(--text-2)" }}>
                                        <Check size={12} className="mt-0.5 flex-shrink-0" style={{ color: "var(--green, #10B981)" }} /> {p}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <div className="mono text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: "var(--red, #EF4444)" }}>✗ Limiti</div>
                            <ul className="space-y-1">
                                {opt.cons.map((c, i) => (
                                    <li key={i} className="text-[12.5px] flex items-start gap-1.5" style={{ color: "var(--text-2)" }}>
                                        <X size={12} className="mt-0.5 flex-shrink-0" style={{ color: "var(--red, #EF4444)" }} /> {c}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="md:col-span-2">
                            <div className="mono text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: opt.color }}>📋 Come configurare</div>
                            <ol className="space-y-2">
                                {opt.steps.map((step, i) => (
                                    <li key={i} className="text-[12.5px] flex items-start gap-2.5" style={{ color: "var(--text)" }}>
                                        <span className="mono text-[10px] font-extrabold rounded-full w-5 h-5 grid place-items-center text-white flex-shrink-0" style={{ background: opt.color }}>{i + 1}</span>
                                        <span className="leading-relaxed">{step.includes("http") ? step.split(/(https?:\/\/\S+)/).map((part, j) =>
                                            part.startsWith("http") ? <a key={j} href={part} target="_blank" rel="noreferrer" className="underline font-bold" style={{ color: opt.color }}>{part}</a> : part
                                        ) : step}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function StorageSection({ s, update }) {
    const current = s.storage_provider || "manual";
    const [status, setStatus] = useState({ provider: current, gdrive: { connected: false } });
    const [busy, setBusy] = useState(false);
    const [banner, setBanner] = useState(() => {
        if (typeof window === "undefined") return "";
        const params = new URLSearchParams(window.location.search);
        const g = params.get("gdrive");
        if (g === "connected") return "✅ Google Drive collegato! I prossimi ordini salveranno i file nel tuo Drive.";
        if (g === "missing_scopes") return "⚠️ Non hai concesso tutti i permessi. Riprova e clicca 'Consenti'.";
        if (g === "error") return "⚠️ Si è verificato un errore durante il collegamento. Riprova.";
        if (g === "invalid_state") return "⚠️ Sessione scaduta. Riprova il collegamento.";
        return "";
    });

    const refresh = async () => {
        try {
            const { data } = await api.get("/tenant/storage/status");
            setStatus(data);
        } catch { /* silent */ }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/tenant/storage/status");
                if (!cancelled) setStatus(data);
            } catch { /* silent */ }
        })();
        // Clean OAuth query params from URL
        if (typeof window !== "undefined" && window.location.search.includes("gdrive=")) {
            window.history.replaceState({}, "", window.location.pathname);
        }
        return () => { cancelled = true; };
    }, []);

    const connectGdrive = async () => {
        setBusy(true); setBanner("");
        try {
            const { data } = await api.get("/oauth/gdrive/start");
            window.location.href = data.url;
        } catch (e) {
            setBanner(`⚠️ ${formatApiError(e)}`);
            setBusy(false);
        }
    };

    const disconnectGdrive = async () => {
        if (!window.confirm("Scollegare Google Drive? I file degli ordini futuri non verranno più salvati lì.")) return;
        setBusy(true);
        try {
            await api.post("/oauth/gdrive/disconnect");
            await refresh();
            update("storage_provider", "manual");
            setBanner("Drive scollegato. Storage tornato su 'Download manuale'.");
        } catch (e) {
            setBanner(`⚠️ ${formatApiError(e)}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="card-tpl p-6" data-testid="storage-section">
            <div className="flex items-start gap-3 mb-1">
                <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: "var(--mod-accent-soft)" }}>
                    <HardDrive size={18} style={{ color: "var(--mod-accent)" }} />
                </div>
                <div>
                    <h3 className="text-[16px] font-extrabold tracking-tight" style={{ color: "var(--navy)" }}>Archiviazione file</h3>
                    <p className="text-[13px] mt-0.5" style={{ color: "var(--text-2)" }}>Scegli dove salvare i file caricati dai tuoi clienti (loghi, PDF, immagini). Puoi cambiare quando vuoi.</p>
                </div>
            </div>

            {banner && (
                <div className="mt-4 p-3 rounded-lg text-[12.5px] font-bold" style={{
                    background: banner.startsWith("✅") ? "#10B98115" : "#FEF3C7",
                    color: banner.startsWith("✅") ? "#065F46" : "#92400E",
                }} data-testid="storage-banner">{banner}</div>
            )}

            <div className="mt-5 p-3 rounded-lg text-[12.5px] flex items-start gap-2" style={{ background: "var(--cobalt-soft)", color: "var(--cobalt)" }}>
                <BookOpen size={14} className="mt-0.5 flex-shrink-0" />
                <span>
                    <b>Come funziona:</b> quando un cliente invia un ordine dalla landing page o tramite Telegram, i file vengono salvati nel posto che scegli qui sotto. <b>Download manuale</b> e <b>Google Drive</b> sono pronti all&apos;uso. Dropbox e OneDrive arrivano nelle prossime release.
                </span>
            </div>

            <div className="mt-5 space-y-3">
                {STORAGE_OPTIONS.map((opt) => (
                    <StorageCard
                        key={opt.id}
                        opt={opt}
                        selected={current}
                        onSelect={(id) => update("storage_provider", id)}
                        oauthStatus={opt.id === "gdrive" ? status.gdrive : null}
                        onConnect={opt.id === "gdrive" ? connectGdrive : undefined}
                        onDisconnect={opt.id === "gdrive" ? disconnectGdrive : undefined}
                        busy={busy && opt.id === "gdrive"}
                    />
                ))}
            </div>
        </div>
    );
}



function ChipInput({ values, onChange, placeholder, testid }) {
    const [input, setInput] = useState("");
    const add = () => {
        if (!input.trim()) return;
        onChange([...values, input.trim()]);
        setInput("");
    };
    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-1.5">
                {values.map((v, i) => (
                    <span key={i} className="inline-flex items-center gap-1 mono text-[11px] uppercase tracking-wider font-semibold px-2 py-1 rounded" style={{ background: "var(--cobalt-soft)", color: "var(--cobalt)" }}>
                        {v}
                        <button onClick={() => onChange(values.filter((_, j) => j !== i))} className="hover:opacity-70"><X size={10} /></button>
                    </span>
                ))}
            </div>
            <div className="flex gap-2">
                <input
                    data-testid={testid}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                    placeholder={placeholder}
                    className="input"
                />
                <button type="button" onClick={add} className="px-3 rounded-lg" style={{ background: "var(--cobalt-soft)", color: "var(--cobalt)" }}>
                    <Plus size={14} />
                </button>
            </div>
        </div>
    );
}

export default function Configurazione() {
    const { user, refreshMe } = useAuth();
    const [settings, setSettings] = useState(null);
    const [users, setUsers] = useState([]);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState("");
    const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "operator" });

    const load = async () => {
        const [t, u] = await Promise.all([api.get("/tenant/settings"), api.get("/users")]);
        setSettings(t.data);
        setUsers(u.data);
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [t, u] = await Promise.all([api.get("/tenant/settings"), api.get("/users")]);
                if (!cancelled) { setSettings(t.data); setUsers(u.data); }
            } catch (e) {
                if (!cancelled) setError(formatApiError(e));
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (!settings) return <div className="text-center py-12" style={{ color: "var(--muted)" }}>Caricamento...</div>;

    const s = settings.settings || {};

    const update = (key, value) => {
        setSettings(prev => ({ ...prev, settings: { ...prev.settings, [key]: value } }));
    };

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            await api.put("/tenant/settings", settings.settings);
            setSaved(true);
            await refreshMe();
            setTimeout(() => setSaved(false), 2000);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const createUser = async () => {
        setError("");
        try {
            await api.post("/users", newUser);
            setNewUser({ email: "", password: "", name: "", role: "operator" });
            load();
        } catch (err) {
            setError(formatApiError(err));
        }
    };

    const deleteUser = async (id) => {
        if (!window.confirm("Eliminare l'utente?")) return;
        await api.delete(`/users/${id}`);
        load();
    };

    return (
        <div className="space-y-6 max-w-4xl" data-testid="config-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1
                        className="tracking-tight"
                        style={{
                            color: "var(--navy)",
                            fontFamily: "Geist, Outfit, sans-serif",
                            fontSize: "clamp(1.7rem, 2.8vw, 2.2rem)",
                            fontWeight: 200,
                            letterSpacing: "-0.035em",
                            lineHeight: 1.05,
                        }}
                    >
                        Confi<span style={{ fontWeight: 600 }}>gurazione</span>
                    </h1>
                    <p className="text-[13px] mt-1" style={{ color: "var(--text-2)" }}>Personalizza TotalPL per la tua tipografia. Le modifiche sono visibili a tutti gli operatori.</p>
                </div>
                <button data-testid="config-save" onClick={save} disabled={saving} className="btn-grad px-5 py-2.5 rounded-lg text-[13px] font-bold flex items-center gap-2">
                    {saving ? "Salvataggio..." : saved ? <><Check size={14} /> Salvato</> : <><Save size={14} /> Salva configurazione</>}
                </button>
            </div>

            {error && (
                <div className="p-3 rounded-lg text-[13px]" style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", color: "#B91C1C" }}>{error}</div>
            )}

            {/* Dati Azienda */}
            <Section icon={Building2} title="Dati Azienda" desc="Le informazioni della tua tipografia. Compariranno su preventivi, fatture e nell'area cliente.">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label>Ragione sociale</Label>
                        <input data-testid="cfg-company" value={s.company_name || ""} onChange={(e) => update("company_name", e.target.value)} className="input" />
                    </div>
                    <div>
                        <Label hint="11 caratteri">P.IVA</Label>
                        <input data-testid="cfg-vat" value={s.vat || ""} onChange={(e) => update("vat", e.target.value)} placeholder="IT01234567890" className="input" />
                    </div>
                    <div className="col-span-2">
                        <Label>Indirizzo</Label>
                        <input data-testid="cfg-address" value={s.address || ""} onChange={(e) => update("address", e.target.value)} placeholder="Via Roma 123, Siano (SA)" className="input" />
                    </div>
                    <div>
                        <Label>Telefono</Label>
                        <input data-testid="cfg-phone" value={s.phone || ""} onChange={(e) => update("phone", e.target.value)} className="input" />
                    </div>
                    <div>
                        <Label>Email</Label>
                        <input data-testid="cfg-email" type="email" value={s.email || ""} onChange={(e) => update("email", e.target.value)} className="input" />
                    </div>
                </div>
            </Section>

            {/* Reparti */}
            <Section icon={Building2} title="Reparti" desc="I reparti della tua tipografia. Vengono usati come categorie nelle commesse e per le statistiche di saturazione.">
                <Label>Reparti attivi</Label>
                <ChipInput
                    values={s.departments || []}
                    onChange={(v) => update("departments", v)}
                    placeholder="es. Grande Formato, Offset, Digitale, Allestimenti..."
                    testid="cfg-departments-input"
                />
                <p className="text-[12px] mt-3 p-3 rounded-lg" style={{ background: "var(--cobalt-soft)", color: "var(--cobalt)" }}>
                    💡 <b>Suggerimento</b>: crea reparti specifici. Esempio per stampa grande formato: &ldquo;Plot&rdquo;, &ldquo;Laminazione&rdquo;, &ldquo;Taglio&rdquo;, &ldquo;Allestimento&rdquo;.
                </p>
            </Section>

            {/* AI Assistant */}
            <Section icon={Sparkles} title="AI Assistant" desc="Aiuta l'AI a capire meglio il tuo lavoro. Più dettagli dai, più precisa sarà nell'estrarre i dati dalle richieste dei clienti (disponibile in Fase 2).">
                <div className="space-y-4">
                    <div>
                        <Label>Specializzazione azienda</Label>
                        <textarea
                            data-testid="cfg-ai-description"
                            rows={3}
                            value={s.ai_company_description || ""}
                            onChange={(e) => update("ai_company_description", e.target.value)}
                            placeholder="Es. Siamo specializzati in stampa di grande formato rigido (forex, d-bond, plexiglass) e flessibile (banner, mesh, canvas)..."
                            className="input"
                        />
                    </div>
                    <div>
                        <Label>Parole chiave urgenza</Label>
                        <p className="text-[12px] mb-2" style={{ color: "var(--muted)" }}>Se compaiono in un messaggio, l&apos;AI imposterà priorità ALTA automaticamente.</p>
                        <ChipInput
                            values={s.urgent_keywords || []}
                            onChange={(v) => update("urgent_keywords", v)}
                            placeholder="es. urgente, oggi, asap, evento..."
                            testid="cfg-urgent-input"
                        />
                    </div>
                </div>
            </Section>

            {/* Archiviazione File */}
            <StorageSection s={s} update={update} />

            {/* Esporta i miei dati */}
            <div className="card-tpl p-6" data-testid="export-section">
                <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: "var(--mod-accent-soft)" }}>
                        <Download size={18} style={{ color: "var(--mod-accent)" }} />
                    </div>
                    <div>
                        <h3 className="text-[16px] font-extrabold tracking-tight" style={{ color: "var(--navy)" }}>Esporta tutti i tuoi dati</h3>
                        <p className="text-[13px] mt-0.5" style={{ color: "var(--text-2)" }}>Scarica un archivio ZIP con commesse, clienti, configurazioni e file allegati. Utile per backup o cancellazione account.</p>
                    </div>
                </div>
                <a href={`${""}/api/tenant/export.zip`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-extrabold text-[13px] text-white" style={{ background: "var(--navy)" }} data-testid="export-zip-btn">
                    <Download size={14} /> Scarica ZIP completo
                </a>
            </div>

            {/* Bot Telegram (placeholder Fase 2) */}
            <Section icon={Bot} title="Bot Telegram" desc="La configurazione completa del Bot Telegram è ora nella sezione dedicata.">
                <a href="/telegram" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-[13px]" style={{ background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))", color: "#fff" }}>
                    <Bot size={14} /> Vai a Bot Telegram →
                </a>
            </Section>

            {/* Utenti & Ruoli */}
            <Section icon={Users} title="Utenti & Ruoli" desc="Operatori, amministratori e clienti che possono accedere al tuo workspace.">
                <div className="mb-4">
                    <Label>Aggiungi nuovo utente</Label>
                    <div className="grid grid-cols-12 gap-2">
                        <input data-testid="newuser-name" value={newUser.name} onChange={(e) => setNewUser(p => ({...p, name: e.target.value}))} placeholder="Nome" className="input col-span-3" />
                        <input data-testid="newuser-email" type="email" value={newUser.email} onChange={(e) => setNewUser(p => ({...p, email: e.target.value}))} placeholder="email@..." className="input col-span-4" />
                        <input data-testid="newuser-password" type="password" value={newUser.password} onChange={(e) => setNewUser(p => ({...p, password: e.target.value}))} placeholder="Password" className="input col-span-2" />
                        <select data-testid="newuser-role" value={newUser.role} onChange={(e) => setNewUser(p => ({...p, role: e.target.value}))} className="input col-span-2">
                            <option value="operator">Operatore</option>
                            <option value="admin">Admin</option>
                            <option value="client">Cliente</option>
                        </select>
                        <button data-testid="newuser-add" onClick={createUser} className="btn-grad rounded-lg font-bold text-[12px] col-span-1">
                            <Plus size={14} className="mx-auto" />
                        </button>
                    </div>
                </div>

                <div className="space-y-1.5">
                    {users.map((u) => (
                        <div key={u.id} data-testid={`user-${u.id}`} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: "var(--bg)" }}>
                            <div className="w-8 h-8 rounded-md grid place-items-center text-white text-[11px] font-bold" style={{ background: u.role === "admin" ? "var(--navy)" : u.role === "operator" ? "var(--cobalt)" : "var(--cyan)" }}>
                                {(u.name || "??").split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-bold" style={{ color: "var(--navy)" }}>{u.name}</div>
                                <div className="text-[11px] truncate" style={{ color: "var(--muted)" }}>{u.email}</div>
                            </div>
                            <span className="mono text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded" style={{ background: "var(--surface)", border: "1px solid var(--line)", color: "var(--text-2)" }}>{u.role}</span>
                            {u.id !== user?.id && (
                                <button onClick={() => deleteUser(u.id)} className="text-[11px]" style={{ color: "var(--red)" }}><X size={14} /></button>
                            )}
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
}
